import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { PortalShell } from './components/PortalShell';
import { Button, Spinner } from './components/ui';
import { usePortal } from './lib/portal';
import { useSession } from './lib/session';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { CatalogDocumentPage } from './pages/CatalogDocumentPage';
import { CatalogPage } from './pages/CatalogPage';
import { ChangeRequestPage } from './pages/ChangeRequestPage';
import { ChangeRequestsPage } from './pages/ChangeRequestsPage';
import { DashboardPage } from './pages/DashboardPage';
import { DocumentEditPage } from './pages/DocumentEditPage';
import { DocumentPage } from './pages/DocumentPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PortalSignInPage } from './pages/PortalSignInPage';
import { SettingsPage } from './pages/SettingsPage';
import { SignInPage } from './pages/SignInPage';
import { TemplatesPage } from './pages/TemplatesPage';

export function App() {
  const { session, loading, unreachable, retry } = useSession();
  const portal = usePortal();

  if (loading || portal.loading) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner label="Loading…" />
      </div>
    );
  }

  // A server we cannot reach is not the same as being signed out: showing the
  // sign-in page here would discard the user's place and imply they were logged out.
  if (unreachable) {
    return (
      <div className="grid h-full place-items-center p-6">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-lg font-semibold text-ink-900">Cannot reach the server</h1>
          <p className="mt-1.5 text-[13.5px] text-ink-500">
            You are still signed in. Check your connection and try again — nothing you have saved is lost.
          </p>
          <Button variant="primary" className="mt-4" onClick={retry}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  /*
   * A portal hostname gets the catalog and nothing else. The API refuses
   * authoring on these hosts regardless, so the routes simply match.
   */
  if (portal.isPortal) {
    if (!session) {
      return (
        <Routes>
          <Route path="*" element={<PortalSignInPage />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route element={<PortalShell />}>
          <Route index element={<CatalogPage />} />
          <Route path="catalog" element={<Navigate to="/" replace />} />
          <Route path=":key" element={<CatalogDocumentPage />} />
          <Route path="catalog/:key" element={<CatalogDocumentPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    );
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="*" element={<SignInPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="documents/:id" element={<DocumentPage />} />
        <Route path="documents/:id/edit" element={<DocumentEditPage />} />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="catalog/:key" element={<CatalogDocumentPage />} />
        <Route path="change-requests" element={<ChangeRequestsPage />} />
        <Route path="change-requests/:id" element={<ChangeRequestPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/:tab" element={<SettingsPage />} />
        <Route path="signin" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
